import { useMemo, useState } from 'react';
import { Check, Info, Plus, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import type { AssessmentPatientContext } from '../types';
import {
  ONE_RM_MUSCLE_GROUP_OPTIONS,
  ONE_RM_OTHER_OPTION,
  ONE_RM_PROTOCOL_STEPS,
  ONE_RM_REFERENCE_NOTES,
  buildOneRMPayload,
  calculateMayhew1RM,
  calculateMeanEstimated1RM,
  calculateWathen1RM,
  formatOneRMKg,
  summarizeOneRMPayload,
  type OneRMResultPayload,
} from '../one-rm/oneRmDomain';

interface OneRMTestProps {
  initial?: OneRMResultPayload | null;
  patientContext?: AssessmentPatientContext;
  onCancel?: () => void;
  onNext: (result: OneRMResultPayload) => void;
}

type OneRMFormEntry = {
  id: string;
  muscleGroupOption: string;
  customMuscleGroup: string;
  direct1RMKg: string;
  submaxLoadKg: string;
  repetitions: string;
  notes: string;
};

export type { OneRMResultPayload };

const createEntryId = () => `one-rm-${Math.random().toString(36).slice(2, 10)}`;

const createEmptyEntry = (): OneRMFormEntry => ({
  id: createEntryId(),
  muscleGroupOption: '',
  customMuscleGroup: '',
  direct1RMKg: '',
  submaxLoadKg: '',
  repetitions: '',
  notes: '',
});

const resolveInitialMuscleGroupFields = (muscleGroup: string) => {
  const normalized = muscleGroup.trim();
  if (ONE_RM_MUSCLE_GROUP_OPTIONS.includes(normalized)) {
    return {
      muscleGroupOption: normalized,
      customMuscleGroup: '',
    };
  }

  return {
    muscleGroupOption: normalized ? ONE_RM_OTHER_OPTION : '',
    customMuscleGroup: normalized,
  };
};

const buildInitialEntries = (initial?: OneRMResultPayload | null): OneRMFormEntry[] => {
  if (!initial?.entries?.length) return [createEmptyEntry()];
  return initial.entries.map((entry) => ({
    id: entry.id || createEntryId(),
    ...resolveInitialMuscleGroupFields(entry.muscleGroup),
    direct1RMKg: entry.direct1RMKg != null ? String(entry.direct1RMKg).replace('.', ',') : '',
    submaxLoadKg: entry.submaxLoadKg != null ? String(entry.submaxLoadKg).replace('.', ',') : '',
    repetitions: entry.repetitions != null ? String(entry.repetitions) : '',
    notes: entry.notes ?? '',
  }));
};

const getResolvedMuscleGroup = (entry: OneRMFormEntry): string =>
  entry.muscleGroupOption === ONE_RM_OTHER_OPTION
    ? entry.customMuscleGroup.trim()
    : entry.muscleGroupOption.trim();

const toInputNumber = (value: string): number | null => {
  const trimmed = value.trim().replace(',', '.');
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const toInputRepetitions = (value: string): number | null => {
  const parsed = toInputNumber(value);
  if (parsed == null) return null;
  const rounded = Math.round(parsed);
  return rounded >= 1 && rounded <= 10 ? rounded : null;
};

export default function OneRMTest({ initial = null, patientContext, onCancel, onNext }: OneRMTestProps) {
  const [entries, setEntries] = useState<OneRMFormEntry[]>(buildInitialEntries(initial));
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const isEditing = Boolean(initial?.entries?.length);

  const payloadPreview = useMemo(
    () =>
      buildOneRMPayload({
        entries: entries.map((entry) => ({
          id: entry.id,
          muscleGroup: getResolvedMuscleGroup(entry),
          direct1RMKg: toInputNumber(entry.direct1RMKg),
          submaxLoadKg: toInputNumber(entry.submaxLoadKg),
          repetitions: toInputRepetitions(entry.repetitions),
          notes: entry.notes,
        })),
        notes,
        patientContext,
      }),
    [entries, notes, patientContext],
  );

  const summary = useMemo(() => summarizeOneRMPayload(payloadPreview), [payloadPreview]);

  const updateEntry = (entryId: string, field: keyof OneRMFormEntry, value: string) => {
    setEntries((current) =>
      current.map((entry) => (entry.id === entryId ? { ...entry, [field]: value } : entry)),
    );
  };

  const updateMuscleGroupOption = (entryId: string, value: string) => {
    setEntries((current) =>
      current.map((entry) =>
        entry.id === entryId
          ? {
              ...entry,
              muscleGroupOption: value,
              customMuscleGroup: value === ONE_RM_OTHER_OPTION ? entry.customMuscleGroup : '',
            }
          : entry,
      ),
    );
  };

  const addEntry = () => {
    setEntries((current) => [...current, createEmptyEntry()]);
  };

  const removeEntry = (entryId: string) => {
    setEntries((current) => {
      if (current.length === 1) {
        return [createEmptyEntry()];
      }
      return current.filter((entry) => entry.id !== entryId);
    });
  };

  const resolveEstimates = (entry: OneRMFormEntry) => {
    const load = toInputNumber(entry.submaxLoadKg);
    const repetitions = toInputRepetitions(entry.repetitions);
    return {
      mayhew: calculateMayhew1RM(load, repetitions),
      wathen: calculateWathen1RM(load, repetitions),
      mean: calculateMeanEstimated1RM(load, repetitions),
    };
  };

  const handleFinish = () => {
    const result = buildOneRMPayload({
      entries: entries.map((entry) => ({
        id: entry.id,
        muscleGroup: getResolvedMuscleGroup(entry),
        direct1RMKg: toInputNumber(entry.direct1RMKg),
        submaxLoadKg: toInputNumber(entry.submaxLoadKg),
        repetitions: toInputRepetitions(entry.repetitions),
        notes: entry.notes,
      })),
      notes,
      patientContext,
      finishedAt: initial?.finishedAt ?? new Date().toISOString(),
    });

    if (!result) {
      toast.error('Registre pelo menos um grupo muscular com 1RM direto ou estimado.');
      return;
    }

    onNext(result);
  };

  return (
    <div className="space-y-4">
      {isEditing ? (
        <div className="sticky top-0 z-20 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-primary-100 bg-white/95 px-4 py-3 shadow-sm backdrop-blur">
          <div>
            <div className="text-sm font-semibold text-slate-900">Editor de 1 repetição máxima</div>
            <div className="text-xs text-slate-500">Inclua ou altere grupos musculares e salve as mudanças.</div>
          </div>
          <button
            type="button"
            onClick={handleFinish}
            className="inline-flex items-center justify-center rounded-xl bg-primary-700 px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-800"
          >
            <Check className="mr-2 h-4 w-4" />
            Salvar alterações
          </button>
        </div>
      ) : null}

      <div className="flex items-start gap-2 rounded-md bg-blue-50 p-3 text-blue-900">
        <Info className="mt-0.5 h-5 w-5" />
        <div className="space-y-2">
          <div>
            <p className="font-medium">1 repetição máxima (1RM)</p>
            <p className="text-sm opacity-90">
              Registre o 1RM medido diretamente e/ou estime o 1RM a partir de uma carga submáxima com no máximo 10 repetições.
            </p>
          </div>
          <div className="grid gap-1 text-xs text-blue-800 md:grid-cols-2">
            {ONE_RM_PROTOCOL_STEPS.map((step) => (
              <div key={step} className="rounded-md border border-blue-100 bg-white/70 px-2 py-1">
                {step}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        <div className="rounded-lg border bg-white p-4">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Grupos válidos</div>
          <div className="mt-2 text-3xl font-black text-slate-900">{summary.groupCount}</div>
        </div>
        <div className="rounded-lg border bg-white p-4">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">1RM direto</div>
          <div className="mt-2 text-xl font-black text-slate-900">{summary.directCount}</div>
          <div className="mt-1 text-xs text-slate-500">Melhor: {formatOneRMKg(summary.bestDirect1RMKg)}</div>
        </div>
        <div className="rounded-lg border bg-white p-4">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">1RM estimado</div>
          <div className="mt-2 text-xl font-black text-slate-900">{summary.estimatedCount}</div>
          <div className="mt-1 text-xs text-slate-500">Melhor: {formatOneRMKg(summary.bestEstimated1RMKg)}</div>
        </div>
        <div className="rounded-lg border bg-white p-4">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Com ambos</div>
          <div className="mt-2 text-xl font-black text-slate-900">{summary.bothMethodsCount}</div>
          <div className="mt-1 text-xs text-slate-500">Direto + estimado no mesmo grupo</div>
        </div>
      </div>

      <div className="space-y-4">
        {entries.map((entry, index) => {
          const estimates = resolveEstimates(entry);
          const repetitions = toInputRepetitions(entry.repetitions);
          return (
            <div key={entry.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="mb-4 flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-slate-900">Grupo muscular {index + 1}</div>
                  <div className="text-xs text-slate-500">Preencha 1RM direto, estimado ou ambos.</div>
                </div>
                <button
                  type="button"
                  onClick={() => removeEntry(entry.id)}
                  className="inline-flex items-center rounded-md border border-rose-200 px-3 py-2 text-sm font-medium text-rose-700 hover:bg-rose-50"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Remover
                </button>
              </div>

              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                <label className="block">
                  <span className="mb-1 block text-sm font-medium text-gray-700">Grupo muscular</span>
                  <select
                    className="w-full rounded-md border-gray-300 focus:border-primary-500 focus:ring-primary-500"
                    value={entry.muscleGroupOption}
                    onChange={(event) => updateMuscleGroupOption(entry.id, event.target.value)}
                  >
                    <option value="">Selecione...</option>
                    {ONE_RM_MUSCLE_GROUP_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                    <option value={ONE_RM_OTHER_OPTION}>{ONE_RM_OTHER_OPTION}</option>
                  </select>
                </label>

                {entry.muscleGroupOption === ONE_RM_OTHER_OPTION ? (
                  <label className="block">
                    <span className="mb-1 block text-sm font-medium text-gray-700">Escreva o grupo muscular</span>
                    <input
                      type="text"
                      className="w-full rounded-md border-gray-300 focus:border-primary-500 focus:ring-primary-500"
                      value={entry.customMuscleGroup}
                      onChange={(event) => updateEntry(entry.id, 'customMuscleGroup', event.target.value)}
                      placeholder="Ex.: glúteo máximo, sóleo..."
                    />
                  </label>
                ) : null}

                <label className="block">
                  <span className="mb-1 block text-sm font-medium text-gray-700">1RM medido diretamente (kg)</span>
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    className="w-full rounded-md border-gray-300 focus:border-primary-500 focus:ring-primary-500"
                    value={entry.direct1RMKg}
                    onChange={(event) => updateEntry(entry.id, 'direct1RMKg', event.target.value)}
                    placeholder="Ex.: 72,5"
                  />
                </label>

                <label className="block">
                  <span className="mb-1 block text-sm font-medium text-gray-700">1RM estimado por carga submáxima (kg)</span>
                  <input
                    type="text"
                    readOnly
                    value={estimates.mean != null ? estimates.mean.toFixed(1).replace('.', ',') : ''}
                    className="w-full rounded-md border-slate-200 bg-slate-50 text-slate-700"
                    placeholder="Calculado automaticamente"
                  />
                </label>

                <label className="block">
                  <span className="mb-1 block text-sm font-medium text-gray-700">Carga submáxima (kg)</span>
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    className="w-full rounded-md border-gray-300 focus:border-primary-500 focus:ring-primary-500"
                    value={entry.submaxLoadKg}
                    onChange={(event) => updateEntry(entry.id, 'submaxLoadKg', event.target.value)}
                    placeholder="Ex.: 55"
                  />
                </label>

                <label className="block">
                  <span className="mb-1 block text-sm font-medium text-gray-700">Repetições realizadas</span>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    step="1"
                    className="w-full rounded-md border-gray-300 focus:border-primary-500 focus:ring-primary-500"
                    value={entry.repetitions}
                    onChange={(event) => updateEntry(entry.id, 'repetitions', event.target.value)}
                    placeholder="1 a 10"
                  />
                  <span className="mt-1 block text-xs text-slate-500">A referência usada para estimativa é válida para até 10 repetições.</span>
                </label>

                <label className="block xl:col-span-1">
                  <span className="mb-1 block text-sm font-medium text-gray-700">Observações do grupo (opcional)</span>
                  <input
                    type="text"
                    className="w-full rounded-md border-gray-300 focus:border-primary-500 focus:ring-primary-500"
                    value={entry.notes}
                    onChange={(event) => updateEntry(entry.id, 'notes', event.target.value)}
                    placeholder="Ex.: técnica padronizada, halter, máquina..."
                  />
                </label>
              </div>

              <div className="mt-4 grid gap-3 md:grid-cols-3">
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Mayhew</div>
                  <div className="mt-2 text-lg font-bold text-slate-900">{formatOneRMKg(estimates.mayhew)}</div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Wathen</div>
                  <div className="mt-2 text-lg font-bold text-slate-900">{formatOneRMKg(estimates.wathen)}</div>
                </div>
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                  <div className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700">Estimativa final</div>
                  <div className="mt-2 text-lg font-bold text-emerald-800">{formatOneRMKg(estimates.mean)}</div>
                  <div className="mt-1 text-xs text-emerald-700">Média entre Mayhew e Wathen</div>
                </div>
              </div>

              {entry.submaxLoadKg && entry.repetitions && repetitions == null ? (
                <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  A estimativa exige número inteiro de 1 a 10 repetições.
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-3">
        <div className="text-sm text-slate-600">Inclua quantos grupos musculares forem necessários na mesma avaliação.</div>
        <button
          type="button"
          onClick={addEntry}
          className="inline-flex items-center rounded-md border border-primary-200 bg-white px-4 py-2 text-sm font-medium text-primary-700 hover:bg-primary-50"
        >
          <Plus className="mr-2 h-4 w-4" />
          Adicionar grupo muscular
        </button>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="mb-2 text-sm font-semibold text-slate-900">Equações de referência</div>
        <div className="space-y-1 text-xs text-slate-600">
          {ONE_RM_REFERENCE_NOTES.map((note) => (
            <div key={note}>{note}</div>
          ))}
        </div>
      </div>

      <label className="block">
        <span className="mb-1 block text-sm font-medium text-gray-700">Observações gerais (opcional)</span>
        <textarea
          rows={3}
          className="w-full rounded-md border-gray-300 focus:border-primary-500 focus:ring-primary-500"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          placeholder="Ex.: exercícios utilizados, observações de técnica, limitações do teste..."
        />
      </label>

      <div className="flex flex-col-reverse gap-3 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900"
        >
          Cancelar
        </button>

        <button
          type="button"
          onClick={handleFinish}
          className="inline-flex items-center justify-center rounded-md bg-primary-700 px-4 py-2 text-sm font-medium text-white hover:bg-primary-800"
        >
          <Check className="mr-2 h-4 w-4" />
          {isEditing ? 'Salvar alterações' : 'Concluir 1RM'}
        </button>
      </div>
    </div>
  );
}
